import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import 'vuetify/styles'
import { h } from 'vue'
import type { IconSet, IconProps } from 'vuetify'
import { normalizeIconName, remapMdiAliases } from '@/utils/iconCompat'
import { aliases as mdiAliases } from 'vuetify/iconsets/mdi'

const THEME_STORAGE_KEY = 'app-theme'
const LEGACY_THEME_KEY = 'vuestic-theme'

function resolveInitialTheme(): 'light' | 'dark' {
  const saved = localStorage.getItem(THEME_STORAGE_KEY) ?? localStorage.getItem(LEGACY_THEME_KEY)
  if (saved === 'light' || saved === 'dark') {
    if (!localStorage.getItem(THEME_STORAGE_KEY)) {
      localStorage.setItem(THEME_STORAGE_KEY, saved)
    }
    return saved
  }
  return 'light'
}

const materialSymbols: IconSet = {
  component: (props: IconProps) => {
    const icon = normalizeIconName(String(props.icon ?? ''))

    return h(
      'span',
      {
        class: ['material-symbols-outlined', props.class],
        style: props.style,
        'aria-hidden': 'true',
      },
      icon,
    )
  },
}

const lightTheme = {
  dark: false,
  colors: {
    primary: '#0F2545',
    'on-primary': '#FFFFFF',
    secondary: '#FF6600',
    'on-secondary': '#FFFFFF',
    accent: '#FF6600',
    'on-accent': '#FFFFFF',
    surface: '#FFFFFF',
    'on-surface': '#262824',
    background: '#E9EEF4',
    'on-background': '#262824',
    error: '#E42222',
    success: '#3D9209',
    warning: '#FFD43A',
    info: '#2C82E0',
    focus: '#2C82E0',
    // L0 canvas — page shell
    'background-primary': '#E9EEF4',
    // L1 surface — cards, header, modals
    'background-secondary': '#FFFFFF',
    // L3 navigation — sidebar
    'background-sidebar': '#DDE6F0',
    // L2 inset — table stripes, hover wells, muted panels
    'background-element': '#F4F7FB',
    'background-border': '#C8D4E3',
    'text-primary': '#262824',
    'text-secondary': '#7F828B',
    'text-inverted': '#FFFFFF',
    danger: '#E42222',
  },
}

const darkTheme = {
  dark: true,
  colors: {
    primary: '#1e40af',
    'on-primary': '#FFFFFF',
    secondary: '#f97316',
    'on-secondary': '#FFFFFF',
    accent: '#f97316',
    'on-accent': '#FFFFFF',
    surface: '#151D2E',
    'on-surface': '#f1f5f9',
    background: '#0b1120',
    'on-background': '#f1f5f9',
    error: '#ef4444',
    success: '#22c55e',
    warning: '#fbbf24',
    info: '#38bdf8',
    focus: '#60a5fa',
    // L0 canvas
    'background-primary': '#0B1120',
    // L1 surface
    'background-secondary': '#151D2E',
    // L3 navigation
    'background-sidebar': '#0F1729',
    // L2 inset
    'background-element': '#1C2739',
    'background-border': '#2A3A52',
    'text-primary': '#f1f5f9',
    'text-secondary': '#b8c5d6',
    'text-inverted': '#0b1120',
    danger: '#ef4444',
  },
}

const materialSymbolAliases = remapMdiAliases(mdiAliases)

export const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'materialSymbols',
    aliases: materialSymbolAliases,
    sets: {
      materialSymbols,
    },
  },
  theme: {
    defaultTheme: resolveInitialTheme(),
    themes: {
      light: lightTheme,
      dark: darkTheme,
    },
  },
  defaults: {
    VBtn: {
      rounded: 'lg',
    },
    VCard: {
      rounded: 'lg',
      elevation: 0,
      border: true,
      color: 'background-secondary',
    },
    VTextField: {
      variant: 'outlined',
      density: 'comfortable',
    },
    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
      menuIcon: 'expand_more',
    },
    VAlert: {
      rounded: 'lg',
    },
    VDialog: {
      maxWidth: 560,
    },
  },
})

export { THEME_STORAGE_KEY, LEGACY_THEME_KEY }
